import React, { useState, useRef, useEffect } from 'react';
import { Column } from '@/types/leads/Table';
import { Eye, EyeOff, Columns } from 'lucide-react';
import { cn } from '@/utils/cn';
import { VISTA_TABLE_COLUMN_SECTIONS, VISTA_TABLE_SECTION_ORDER, VISTA_TABLE_SECTION_TITLES, vistaColumnRank } from '@/types/leads/Profile';

interface ColumnVisibilityDropdownProps {
    columns: Column[];
    visibleColumns: Set<string>;
    onToggleColumn: (columnName: string) => void;
    iconOnly?: boolean;
    // Client uses the Vista Form: hide fields it doesn't use and name Owner as in the form.
    vistaForm?: boolean;
}

// Native fields a Vista Form client doesn't use (Status/Source are replaced by Vista Form fields).
export const VISTA_UNUSED_COLUMNS = ['status', 'source', 'message', 'last_remark', 'is_qualified'];

const ColumnVisibilityDropdown: React.FC<ColumnVisibilityDropdownProps> = ({
    columns,
    visibleColumns,
    onToggleColumn,
    iconOnly = false,
    vistaForm = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Group columns by type for better organization
    // Use category field from backend for tracking columns
    const standardColumns = columns.filter(col => !col.is_custom && !col.is_tracking_field
        && !(vistaForm && VISTA_UNUSED_COLUMNS.includes(col.col_name)));

    // Add special computed columns to standard columns
    const computedColumns = vistaForm
        ? [{ col_name: 'owner', col_label: 'Counsellor Assigned', col_type: 'text', is_custom: false, uuid: 'owner-col' }]
        : [
            { col_name: 'owner', col_label: 'Owner', col_type: 'text', is_custom: false, uuid: 'owner-col' },
            { col_name: 'last_remark', col_label: 'Last Remark', col_type: 'text', is_custom: false, uuid: 'last-remark-col' }
        ];

    const allStandardColumns = [...standardColumns, ...computedColumns];
    const customColumns = columns.filter(col => col.is_custom);

    // Group tracking columns by their category from backend
    const utmColumns = columns.filter(col => col.category === 'UTM Tracking');
    const fbColumns = columns.filter(col => col.category === 'Facebook Tracking');
    const liColumns = columns.filter(col => col.category === 'LinkedIn Tracking');

    const renderColumnGroup = (groupColumns: Column[], groupTitle?: string) => (
        <div>
            {groupTitle && (
                <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100">
                    {groupTitle}
                </div>
            )}
            <div>
                {groupColumns.map(column => {
                    const isVisible = visibleColumns.has(column.col_name);
                    const isPhoneColumn = column.col_name === 'phone'; // Phone is always fixed
                    
                    return (
                        <label
                            key={column.col_name}
                            className={cn(
                                "flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-50",
                                isPhoneColumn && "opacity-50 cursor-not-allowed"
                            )}
                        >
                            <input
                                type="checkbox"
                                checked={isVisible || isPhoneColumn}
                                onChange={() => !isPhoneColumn && onToggleColumn(column.col_name)}
                                disabled={isPhoneColumn}
                                className="w-4 h-4 text-brandColor border-gray-300 rounded focus:ring-brandColor mr-3"
                            />
                            <div className="flex-1">
                                <div className="flex items-center justify-between">
                                    <span className={cn(
                                        "font-medium",
                                        isPhoneColumn ? "text-gray-400" : "text-gray-900"
                                    )}>
                                        {column.col_label}
                                    </span>
                                    {isVisible ? (
                                        <Eye className="h-4 w-4 text-green-500" />
                                    ) : (
                                        <EyeOff className="h-4 w-4 text-gray-400" />
                                    )}
                                </div>
                                {isPhoneColumn && (
                                    <p className="text-xs text-gray-400 mt-1">Fixed column</p>
                                )}
                            </div>
                        </label>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    "inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-all",
                    isOpen
                        ? "bg-gray-100 text-gray-700 border-gray-300"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                )}
                title={iconOnly ? `Column Settings (${visibleColumns.size - 1} visible)` : undefined}
            >
                <Columns className="h-4 w-4" />
                {!iconOnly && (
                    <span>Columns ({visibleColumns.size - 1})</span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[80vh] flex flex-col">
                    {/* Header */}
                    <div className="p-3 border-b border-gray-200 flex-shrink-0">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-medium text-gray-900">Column Visibility</h3>
                            <div className="text-xs text-gray-500">
                                {visibleColumns.size - 1} of {columns.length + computedColumns.length - 1} visible
                            </div>
                        </div>
                    </div>

                    {/* Scrollable Content */}
                    <div className="overflow-y-auto flex-1">
                        {/* Vista Form clients: one group per Vista Form section, in the form's order */}
                        {vistaForm && (() => {
                            const sectionCols = [...allStandardColumns, ...customColumns]
                                .slice().sort((a, b) => vistaColumnRank(a.col_name) - vistaColumnRank(b.col_name));
                            const other = sectionCols.filter(c => !VISTA_TABLE_COLUMN_SECTIONS[c.col_name]);
                            return (
                                <>
                                    {VISTA_TABLE_SECTION_ORDER.map((section, idx) => {
                                        const cols = sectionCols.filter(c => VISTA_TABLE_COLUMN_SECTIONS[c.col_name] === section);
                                        return cols.length > 0 ? (
                                            <div key={section} className={idx > 0 ? 'border-t border-gray-100' : ''}>
                                                {renderColumnGroup(cols, VISTA_TABLE_SECTION_TITLES[section] || section)}
                                            </div>
                                        ) : null;
                                    })}
                                    {other.length > 0 && (
                                        <div className="border-t border-gray-100">
                                            {renderColumnGroup(other, 'Other')}
                                        </div>
                                    )}
                                </>
                            );
                        })()}

                        {/* Standard Columns */}
                        {!vistaForm && renderColumnGroup(allStandardColumns, 'Standard Columns')}

                        {/* Custom Columns */}
                        {!vistaForm && customColumns.length > 0 && (
                            <div className="border-t border-gray-100">
                                {renderColumnGroup(customColumns, 'Custom Fields')}
                            </div>
                        )}

                        {/* UTM Columns */}
                        {utmColumns.length > 0 && (
                            <div className="border-t border-gray-100">
                                {renderColumnGroup(utmColumns, 'UTM Tracking')}
                            </div>
                        )}

                        {/* Facebook Columns */}
                        {fbColumns.length > 0 && (
                            <div className="border-t border-gray-100">
                                {renderColumnGroup(fbColumns, 'Facebook Tracking')}
                            </div>
                        )}

                        {/* LinkedIn Columns */}
                        {liColumns.length > 0 && (
                            <div className="border-t border-gray-100">
                                {renderColumnGroup(liColumns, 'LinkedIn Tracking')}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                        <p className="text-xs text-gray-500">
                            Phone column is always fixed and visible for better navigation.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColumnVisibilityDropdown;